import { useCallback, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * One pending Undo: what was removed, and how to put it back
 * (epic PI-SPEC-SW-E294, feature PI-SPEC-SW-1508).
 */
export interface PendingUndo {
  /** Unique per removal; pass it as the UndoNotice `key` so a replacement restarts the countdown. */
  id: string;
  message: ReactNode;
  icon?: LucideIcon;
  /** Puts the removed thing back. Rejects when it could not. */
  undo: () => Promise<void>;
}

export interface UndoNoticeState {
  /** The notice on screen, or null. Never more than one. */
  notice: PendingUndo | null;
  /** True while the current notice's undo callback runs. */
  pending: boolean;
  /** The last undo failure, for an ErrorLine; null when none. Cleared by `show` and `clearError`. */
  error: Error | null;
  /** Shows a notice. Any notice already on screen is replaced; its removal stays done. */
  show: (notice: PendingUndo) => void;
  /** Runs the current notice's undo callback, then clears the notice. A failure lands in `error`. */
  undo: () => Promise<void>;
  /** Clears the notice without undoing (the UndoNotice's onExpire). With an id, only if it is still that notice. */
  dismiss: (id?: string) => void;
  clearError: () => void;
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(typeof value === "string" ? value : "Undo failed.");
}

/**
 * Drives the shared UndoNotice: holds at most one pending removal, replaces it
 * when another removal happens (the earlier one stays removed), runs Undo and
 * reports a failed Undo through `error` so the page can show an ErrorLine.
 *
 * ```tsx
 * const undoNotice = useUndoNotice();
 * {undoNotice.notice && (
 *   <UndoNotice
 *     key={undoNotice.notice.id}
 *     icon={undoNotice.notice.icon}
 *     message={undoNotice.notice.message}
 *     pending={undoNotice.pending}
 *     onUndo={() => void undoNotice.undo()}
 *     onExpire={() => undoNotice.dismiss(undoNotice.notice?.id)}
 *   />
 * )}
 * ```
 */
export function useUndoNotice(): UndoNoticeState {
  const [notice, setNotice] = useState<PendingUndo | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const noticeRef = useRef<PendingUndo | null>(null);
  const inFlightRef = useRef<string | null>(null);

  const replace = useCallback((next: PendingUndo | null) => {
    noticeRef.current = next;
    setNotice(next);
  }, []);

  const show = useCallback(
    (next: PendingUndo) => {
      inFlightRef.current = null;
      setPending(false);
      setError(null);
      replace(next);
    },
    [replace]
  );

  const dismiss = useCallback(
    (id?: string) => {
      if (id !== undefined && noticeRef.current?.id !== id) return;
      replace(null);
    },
    [replace]
  );

  const undo = useCallback(async () => {
    const current = noticeRef.current;
    if (current === null || inFlightRef.current !== null) return;
    inFlightRef.current = current.id;
    setPending(true);
    setError(null);
    try {
      await current.undo();
    } catch (err) {
      // Reported even when a newer notice replaced this one: the area or role is still removed.
      setError(toError(err));
    } finally {
      // A notice shown while this undo ran replaced it; leave the new one alone.
      if (inFlightRef.current === current.id) {
        inFlightRef.current = null;
        setPending(false);
        if (noticeRef.current?.id === current.id) replace(null);
      }
    }
  }, [replace]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { notice, pending, error, show, undo, dismiss, clearError };
}
